import React from 'react';
import _ from 'lodash';
import xss from 'xss';
import linkifyHtml from 'linkify-html';

function titleize(this: string) {
  return _.startCase(_.toLower(this));
}

// Matches flag sequences, base emoji with optional skin-tone / variation
// selector, and ZWJ-joined sequences. Used to wrap emojis so dark mode
// (which inverts the whole page) can counter-invert them.
const EMOJI_REGEX = /\p{Regional_Indicator}\p{Regional_Indicator}|\p{Extended_Pictographic}(?:\p{Emoji_Modifier}|\uFE0F)?(?:\u200D\p{Extended_Pictographic}(?:\p{Emoji_Modifier}|\uFE0F)?)*/gu;

function toReactHtml(this: string, { linkify, highlight }: { linkify?: boolean, highlight?: string } = {}) {
  let html = xss(this, { whiteList: { mark: [], b: [], i: [] } });
  if (highlight) {
    const openingTag = '<mark>';
    const closingTag = '</mark>';
    Array.from(this.matchAll(new RegExp(_.escapeRegExp(highlight), 'ig'))).map(({ index }) => index).forEach((strIndex, ix) => {
      const adjustedIndex = strIndex + ix * (openingTag.length + closingTag.length);
      html = ''
        + html.substring(0, adjustedIndex)
        + openingTag
        + html.substring(adjustedIndex, adjustedIndex + highlight.length)
        + closingTag
        + html.substring(adjustedIndex + highlight.length);
    });
  }
  if (linkify) {
    html = linkifyHtml(html, {
      validate: {
        url: (value: string) => /^https?:\/\/([^"'\[\]\{\};<>]|&amp;)*$/.test(value),
      },
      rel: 'noopener noreferrer',
      target: '_blank',
    });
  }
  html = html.replace(EMOJI_REGEX, (match) => `<span class="emoji">${match}</span>`);
  return { __html: html };
}

// Wraps emoji sequences in a string as <span key=<index> class="emoji"> React nodes,
// so dark mode (which inverts the whole page) can counter-invert them.
// Use for plain-text JSX children that bypass toReactHtml.
export function wrapEmojis(text: string | null | undefined): React.ReactNode {
  if (!text) return text;
  const parts: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(EMOJI_REGEX)) {
    const index = match.index!;
    if (index > last) parts.push(text.slice(last, index));
    parts.push(React.createElement('span', { key: key++, className: 'emoji' }, match[0]));
    last = index + match[0].length;
  }
  if (parts.length === 0) return text;
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function hashCode(this: string) {
  // simple SDBM hash implementation
  return this.split('').reduce(
    (acc, currentVal) =>
      currentVal.charCodeAt(0) + (acc << 6) + (acc << 16) - acc,
    0,
  );
}

declare global {
  interface String {
    titleize: typeof titleize;
    toReactHtml: typeof toReactHtml;
    hashCode: typeof hashCode;
  }
}

Object.assign(String.prototype, { titleize, toReactHtml, hashCode });
